import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import {
  CustomListItem,
  DialogStates,
} from '../../types/import-custom-list.types';
import { CustomListDataService } from '../../services/custom-list-data.service';
import { ImportCustomListsNetworkingService } from '../../services/import-custom-lists-networking.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, Validators } from '@angular/forms';

@Component({
  selector: 'firebird-web-import-list-dialog',
  templateUrl: './import-list-dialog.component.html',
  styleUrls: ['./import-list-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CustomListDataService, ImportCustomListsNetworkingService],
  standalone: false,
})
export class ImportListDialogComponent implements OnInit, OnDestroy {
  public isErrorMsgVisible = false;
  public lists: CustomListItem[] = [];
  public profileControl = new FormControl('', [
    Validators.required,
    Validators.email,
  ]);
  public isProfileError = false;

  public isSelectedAll$ = new BehaviorSubject<boolean>(false);
  public currentState$ = new BehaviorSubject<DialogStates>('loading');
  public closingSubject$ = new Subject<[string[], string]>();

  private destroy$ = new Subject<void>();

  constructor(
    private readonly service: CustomListDataService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.fetchCustomLists();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closingSubject$.complete();
  }

  public trackByList(index: number, list: CustomListItem): string {
    return list.listId;
  }

  public onCheckboxChange(listId: string, { target }: Event): void {
    const isChecked = (target as HTMLInputElement).checked;

    const listItem = this.lists.find(
      (list) => list.listId === listId
    ) as CustomListItem;

    if (listItem) {
      listItem.checked = isChecked;
    }

    if (isChecked) {
      this.isErrorMsgVisible = false;
      const isAllSelected = this.lists.every((list) => list.checked);
      if (isAllSelected) {
        this.isSelectedAll$.next(true);
      }
    } else {
      this.isSelectedAll$.next(false);
    }
  }

  public onConfirm(): void {
    if (this.currentState$.getValue() === 'noAccount') {
      this.onFetchByProfile();
    } else {
      this.onSend();
    }
  }

  public onSelectAllChange({ target }: Event): void {
    if ((target as HTMLInputElement).checked) {
      this.selectUnselect(true);
      this.isErrorMsgVisible = false;
      this.isSelectedAll$.next(true);
    } else {
      this.clearSelectedLists();
    }
  }

  public areAnyCustomLocations(): boolean {
    return (
      this.lists.length > 0 || this.currentState$.getValue() === 'noAccount'
    );
  }

  public cancelButtonText(): string {
    return this.lists.length > 0 ? 'X-Cancel' : 'Close';
  }

  private clearSelectedLists(): void {
    this.selectUnselect(false);
    this.isSelectedAll$.next(false);
  }

  private selectUnselect(isChecked: boolean): void {
    this.lists.forEach((list) => (list.checked = isChecked));
  }

  private fetchCustomLists(): void {
    this.service
      .fetchCustomLists()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (lists) => {
          this.lists = lists;
          this.currentState$.next(
            this.lists.length > 0 ? 'locations' : 'noLocations'
          );
        },
        error: (err: HttpErrorResponse) => {
          this.lists = [];

          if (err.status === 404) {
            this.currentState$.next('noAccount');
          } else if (err.status === 400) {
            this.currentState$.next('multipleAccounts');
          }
        },
      });
  }

  private onSend(): void {
    this.isErrorMsgVisible =
      this.lists?.filter((list) => list.checked).length === 0;

    if (this.isErrorMsgVisible) {
      return;
    }

    this.closingSubject$.next([
      this.lists.filter((list) => list.checked).map((list) => list.listId),
      this.profileControl.value as string,
    ]);
  }

  private onFetchByProfile(): void {
    if (this.profileControl.valid) {
      this.service
        .fetchCustomLists(this.profileControl.value as string)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (lists) => {
            this.lists = lists;
            this.currentState$.next(
              this.lists.length > 0 ? 'locations' : 'noLocations'
            );
            this.isProfileError = false;
            this.cdr.markForCheck();
          },
          error: (err: HttpErrorResponse) => {
            this.lists = [];

            this.isProfileError = true;

            if (err.status === 400) {
              this.currentState$.next('multipleAccounts');
              this.isProfileError = false;
            }

            this.cdr.markForCheck();
          },
        });
    }
  }
}
